import { useCallback } from 'react';
import { useForm } from 'react-hook-form';

import { ActivityBuilderModal } from 'approot/activity-builder/activity-builder-modal';
import {
  LabelStyled,
  TextInputStyled,
} from 'approot/shared/styles/inputs.style';
import { Box } from 'approot/shared/design-system/box/box.style';
import ValidationMessage from 'approot/shared/validation/validation-message';
import { SimpleModalVerticalActionsStyled } from 'lib/modal/simple-modal.styles';
import { NegativeButton } from 'approot/shared/styles/buttons/negative-button';
import Text from 'approot/shared/styles/typography/text';
import { SettingsHr } from '../settings.style';

const MINOR_FIELD = 'minor-field';

type Props = {
  triggerMinorUpdate: () => void;
  closeModal: () => void;
};

const SettingsPublishMinorModal = ({
  triggerMinorUpdate,
  closeModal,
}: Props) => {
  const loading = false;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = useCallback(() => {
    triggerMinorUpdate();
    closeModal();
  }, [triggerMinorUpdate, closeModal]);

  return (
    <ActivityBuilderModal
      title="Publish Minor Update"
      headerTitle="Publish Minor Update"
      isVisible
      onClose={closeModal}
    >
      <Text $colour="red">
        Use a minor update for small refinements, like text tweaks or
        corrections, that don’t affect the structure of the lesson or how a
        student would answer a question
      </Text>
      <Text $fontWeight="medium" $colour="red">
        If your changes affect content or structure, choose a major update
        instead.
      </Text>
      <SettingsHr />

      <form onSubmit={handleSubmit(onSubmit)}>
        <Box $flexDirection="column" $marginBottom={4}>
          <LabelStyled htmlFor={MINOR_FIELD}>
            To confirm, type MINOR below
          </LabelStyled>
          <TextInputStyled
            type="text"
            id={MINOR_FIELD}
            {...register(MINOR_FIELD, {
              validate: value =>
                value.toUpperCase() !== 'MINOR'
                  ? 'The value does not match MINOR'
                  : true,
            })}
            aria-required
            aria-invalid={!!errors?.[MINOR_FIELD]}
            aria-describedby={`${MINOR_FIELD}-error-message`}
            $hasError={!!errors?.[MINOR_FIELD]}
            data-selector="txt-confirm-minor"
          />
          <ValidationMessage
            errors={errors}
            field={MINOR_FIELD}
            id={`${MINOR_FIELD}-error-message`}
          />
        </Box>
        <SimpleModalVerticalActionsStyled>
          <NegativeButton
            type="submit"
            loading={loading}
            disabled={loading}
            data-selector="btn-confirm-minor-update"
          >
            Confirm Minor Update
          </NegativeButton>
        </SimpleModalVerticalActionsStyled>
      </form>
    </ActivityBuilderModal>
  );
};

export default SettingsPublishMinorModal;
